import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Zap } from "lucide-react";
import { cn } from "@/utils/cn";
import toast from "react-hot-toast";

import { useGetMySpaceQuickActivitiesQuery } from "@/services/(my-space)/quickActivityApi";
import { useCheckAttendanceExceptionEligibilityMutation } from "@/services/(my-space)/api.time-attendance";
import { QUICK_ACTIVITY_ICONS } from "@/pages/(home)/(company)/time-attendance/settings/quick-activity/_components/quickActivityIcons";
import TimeAttendanceRuleModal from "@/components/time-attendance";
import type { IQuickActivity } from "@/types/(company)/quickActivity.interface";
import type { IAttendanceExceptionEligibility } from "@/types/(my-space)/timeAttendace.interface";
import { useCurrentLocation } from "@/hooks/useCurrentLocation";
import type { ILocationFix } from "@/utils/geolocation";
import { serverNow } from "@/utils/serverClock";

type Props = {
  value: number | null;
  onChange: (id: number | null) => void;
  isCheckedIn: boolean;
  /** ISO timestamp when current live activity started (only used while user is away). */
  activeStartTime?: string | null;
};

export const QuickActivityButtons = ({
  value,
  onChange,
  isCheckedIn,
  activeStartTime,
}: Props) => {
  const { t } = useTranslation("my-space");
  const { data, isLoading } = useGetMySpaceQuickActivitiesQuery({ isActive: true });
  const activities = data?.data ?? [];

  const { locate, isLocating } = useCurrentLocation();

  const [checkEligibilityAttendanceRule, { isLoading: checkOutLoading }] =
    useCheckAttendanceExceptionEligibilityMutation();

  const [modalOpen, setModalOpen] = useState(false);
  const [eligibilityResponse, setEligibilityResponse] =
    useState<IAttendanceExceptionEligibility>();

  // Persists across modal retries so the quickActivityId is always sent
  const pendingActivityIdRef = useRef<number | null>(null);

  const handleCheckEligibilityAttendanceRule = async (
    location?: ILocationFix | null,
  ) => {
    try {
      const fix = location ?? (await locate());
      const res = await checkEligibilityAttendanceRule({
        lat: fix?.lat ?? null,
        long: fix?.long ?? null,
        accuracy: fix?.accuracy ?? null,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        quickActivityId: pendingActivityIdRef.current,
      }).unwrap();
      setModalOpen(!res.data.isAllowedToPerformAction);
      setEligibilityResponse(res.data);
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? t("toasts.something_went_wrong"));
    }
  };

  const handlePillClick = (activityId: number) => {
    if (value !== null && value !== activityId) return;
    const newId = value === activityId ? null : activityId;
    onChange(newId);
    pendingActivityIdRef.current = newId;
    handleCheckEligibilityAttendanceRule();
  };

  if (isLoading || activities.length === 0) return null;

  return (
    <>
      <div className="flex items-center gap-3 px-3 py-1.5 border-t border-border bg-background rounded-b-md w-full overflow-hidden">
        <div className="flex items-center gap-1.5 shrink-0">
          <Zap size={12} className="text-border-strong" />
          <p className="hidden lg:block text-[10px] font-bold uppercase tracking-widest text-border-strong whitespace-nowrap">
            {t("ui.quick_activity")}
          </p>
        </div>

        <div className="w-px self-stretch bg-muted shrink-0" />

        <div className="flex items-center gap-2 overflow-x-auto flex-1 min-w-0 scrollbar-hidden">
          {activities.map((activity) => {
            const isSelected = value === activity.id;
            const anySelected = value !== null;
            return (
              <ActivityPill
                key={activity.id}
                activity={activity}
                isSelected={isSelected}
                isDisabled={!isCheckedIn || checkOutLoading || isLocating}
                anySelected={anySelected}
                isCheckedIn={Boolean(isCheckedIn)}
                onSelect={() => handlePillClick(activity.id)}
                liveStartTime={
                  isSelected && !isCheckedIn ? (activeStartTime ?? null) : null
                }
              />
            );
          })}
        </div>
      </div>

      <TimeAttendanceRuleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        eligibility={eligibilityResponse}
        handleCheckEligibilityAttendanceRule={
          handleCheckEligibilityAttendanceRule
        }
      />
    </>
  );
};

/* ─── Pill ─────────────────────────────────────────────────── */

type PillProps = {
  activity: IQuickActivity;
  isSelected: boolean;
  isDisabled: boolean;
  anySelected: boolean;
  isCheckedIn: boolean;
  onSelect: () => void;
  /** ISO timestamp — when set, pill shows live-elapsed counter. */
  liveStartTime?: string | null;
};

const formatElapsed = (ms: number): string => {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const useElapsed = (startIso: string | null | undefined): number | null => {
  const [elapsed, setElapsed] = useState<number | null>(
    startIso ? serverNow() - new Date(startIso).getTime() : null,
  );

  useEffect(() => {
    if (!startIso) {
      setElapsed(null);
      return;
    }
    const startMs = new Date(startIso).getTime();
    setElapsed(serverNow() - startMs);
    const id = setInterval(() => setElapsed(serverNow() - startMs), 1000);
    return () => clearInterval(id);
  }, [startIso]);

  return elapsed;
};

const ActivityPill = ({
  activity,
  isSelected,
  isDisabled,
  anySelected,
  isCheckedIn,
  onSelect,
  liveStartTime,
}: PillProps) => {
  const { t } = useTranslation("my-space");
  const elapsedMs = useElapsed(liveStartTime ?? null);

  const iconEntry = QUICK_ACTIVITY_ICONS.find((i) => i.value === activity.icon);
  if (!iconEntry) return null;

  const Icon = iconEntry.icon;
  // Normalise space-separated RGB ("26 183 152") to comma form for rgba()
  const colorRaw = activity.color?.replace(/\s+/g, ", ") ?? "158,158,158";

  const isAway = isDisabled && isSelected;
  const showElapsed = isAway && elapsedMs != null;
  const durationMin = activity.duration ? Number(activity.duration) : 0;
  const isOverdue =
    showElapsed && durationMin > 0
      ? (elapsedMs as number) > durationMin * 60_000
      : false;

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={isDisabled}
      title={
        showElapsed
          ? activity.duration
            ? t("dashboard.activity_elapsed_planned", {
                name: activity.name,
                elapsed: formatElapsed(elapsedMs!),
                planned: activity.duration,
              })
            : t("dashboard.activity_elapsed", {
                name: activity.name,
                elapsed: formatElapsed(elapsedMs!),
              })
          : isAway
            ? t("dashboard.activity_away", { name: activity.name })
            : isDisabled
              ? t("dashboard.activity_needs_checkin")
              : activity.name
      }
      className={cn(
        "relative flex items-center gap-1.5 ps-1 pe-1 lg:pe-2 py-1 rounded-lg border transition-all duration-150 shrink-0",
        isDisabled ? "cursor-not-allowed" : "cursor-pointer",
        isSelected
          ? "bg-[rgb(var(--pill-color))] border-[rgb(var(--pill-color))] hover:bg-[rgba(var(--pill-color),0.85)]"
          : !isCheckedIn || (!isSelected && anySelected)
            ? "bg-[rgba(var(--pill-color),0.08)] border-[rgba(var(--pill-color),0.5)]"
            : "bg-[rgba(var(--pill-color),0.12)] border-[rgba(var(--pill-color),0.8)] hover:bg-[rgba(var(--pill-color),0.2)]",
      )}
      style={{ "--pill-color": colorRaw } as CSSProperties}
    >
      <div
        className={cn(
          "w-4 h-4 rounded-full flex items-center justify-center shrink-0",
          isSelected
            ? "bg-white/20 text-white" : "bg-[rgba(var(--pill-color),0.12)] text-[rgb(var(--pill-color))]",
        )}
      >
        <Icon size={10} />
      </div>

      <span
        className={cn(
          "text-[10px] font-semibold whitespace-nowrap",
          isSelected ? "text-white" : "text-[rgb(var(--pill-color))]",
        )}
      >
        {activity.name}
      </span>

      {showElapsed ? (
        <span
          className={cn(
            "text-[8px] font-semibold px-1 py-px rounded leading-none tabular-nums",
            isOverdue
              ? "bg-red-500 text-white animate-pulse"
              : isSelected
                ? "bg-white/20 text-white" : "bg-[rgba(var(--pill-color),0.12)] text-[rgb(var(--pill-color))]",
          )}
        >
          {formatElapsed(elapsedMs!)}
        </span>
      ) : activity.duration ? (
        <span
          className={cn(
            "text-[8px] font-medium px-1 py-px rounded leading-none",
            isSelected
              ? "bg-white/20 text-white" : "bg-[rgba(var(--pill-color),0.12)] text-[rgb(var(--pill-color))]",
          )}
        >
          {t("common:shared_ui.minutes_short", { minutes: activity.duration })}
        </span>
      ) : null}

      {isSelected && !isAway && (
        <span className="absolute -top-0.5 -end-0.5 w-1.5 h-1.5 rounded-full border border-white animate-pulse bg-[rgb(var(--pill-color))]" />
      )}
      {showElapsed && (
        <span className="absolute -top-0.5 -end-0.5 w-1.5 h-1.5 rounded-full border border-white animate-pulse bg-red-500" />
      )}
    </button>
  );
};
